'use client'
import { marketWorth } from '@/utils/market-metrics'

import React, { useMemo, useRef, useState, useEffect } from 'react'
import BitcoinIcon from '@/icons/bitcoin-filled'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import { SaleMarketplaceIcon } from '@/components/marketplace-icon'
import { formatActivityBtc } from '@/utils/format-activity-btc'
import useInscriptionModal from '@/utils/hooks/useInscriptionModal'
import RuneMarkets from './rune-markets'
import isFungible from '@/utils/is-fungible'
import ExploreLink from './explore-link'
import type { RuneSnapshot } from '@/utils/rune-market-data'
import useSWR from 'swr'
import { API_URL } from '@/utils/constants'
import { correctedGlobalActivityFetcher } from '@/utils/global-activity-fetcher'
import Link from 'next/link'
import {
	Radio
} from 'lucide-react'

import CollectionIcon from '@/components/collection-icon'
import RelativeVolumeBar from '@/components/relative-volume-bar'
import InscriptionContent from '@/components/inscription-content'
import { Collection } from '@/utils/types/collections'
import { getSaleBoughtAt, getSalePriceSats } from '@/utils/sales-activity'

type Period = '7d'
type Protocol = 'all' | 'rune' | 'tap' | 'brc20' | 'alkane'

interface MarketOverviewProps {
 runes?: RuneSnapshot | null
	collections?: Collection[]
	tokens?: Collection[]
	recentSales?: any[]
}

const EMPTY_COLLECTIONS: Collection[] = []
const EMPTY_SALES: any[] = []

const finiteNumber = (value: unknown): number => {
	const parsed = typeof value === 'number' ? value : Number(value)
	return Number.isFinite(parsed) ? parsed : 0
}

const protocolFromSlug = (slug = ''): Exclude<Protocol, 'all'> | 'ordinal' => {
	const normalized = slug.toLowerCase()
	if (normalized.startsWith('rune-')) return 'rune'
	if (normalized.startsWith('tap-')) return 'tap'
	if (normalized.startsWith('brc20-')) return 'brc20'
	if (normalized.startsWith('alkane-')) return 'alkane'
	return 'ordinal'
}

const protocolLabel = (slug = '') => {
	const protocol = protocolFromSlug(slug)
	if (protocol === 'rune') return 'RUNE'
	if (protocol === 'tap') return 'TAP'
	if (protocol === 'brc20') return 'BRC-20'
	if (protocol === 'alkane') return 'ALKANE'
	return 'ORDINAL'
}

const volumeForPeriod = (market: Collection, period: Period, global: boolean) => {
	void period
	return global
		? finiteNumber(market.global_volume_week ?? market.volume_week)
		: finiteNumber(market.volume_week)
}

// The fair price's 7d change, the one number the header and the quick view
// print for the same collection. Nothing else stands in for it: the escrow
// VWAP used to, and a row would show +5% from one metric on one load and
// +178% from the other on the next. Missing stays "—".
const changeForPeriod = (market: Collection, period: Period): number | null => {
	void period
	const fair = market.change_week_fair
	return typeof fair === 'number' && Number.isFinite(fair) ? fair : null
}

const normalizeChange = (change: number | null) => {
	if (change === null) return null
	return Math.abs(change) < 0.005 ? 0 : change
}

const formatVolume = (sats: number) => {
	if (sats <= 0) return '—'
	const btc = sats / 1e8
	if (btc < 0.01) return '<0.01 BTC'
	if (btc >= 100) return `${Math.round(btc).toLocaleString('en-US')} BTC`
	return `${btc.toLocaleString('en-US', {
		minimumFractionDigits: btc < 1 ? 2 : 1,
		maximumFractionDigits: btc < 1 ? 2 : 1
	})} BTC`
}

const formatCount = (value: number) => {
	if (!value) return '—'
	return Intl.NumberFormat('en-US', {
		notation: value >= 10_000 ? 'compact' : 'standard',
		maximumFractionDigits: 1
	}).format(value)
}

const formatSalePrice = (sats: number) => {
	if (sats <= 0) return '—'
	return `${formatActivityBtc(sats)} BTC`
}

const formatSaleTime = (value: string) => {
	if (!value) return 'Recently'
	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return 'Recently'
	return `${date.toLocaleTimeString('en-US', {
		hour: 'numeric',
		minute: '2-digit',
		timeZone: 'UTC'
	})} UTC`
}

const saleName = (sale: any) =>
	sale?.meta?.name || sale?.collection?.name || sale?.id?.slice(0, 10) || 'Ordinal'

const saleCollectionHref = (sale: any) =>
	sale?.collection?.slug ? `/collection/${sale.collection.slug}` : '/activity'

const sortMarkets = (markets: Collection[], period: Period, global: boolean) =>
	markets
		.filter((market) => market?.slug)
		.slice()
		.sort((a, b) => volumeForPeriod(b, period, global) - volumeForPeriod(a, period, global))

const TrendingRail = ({
	collections,
	tokens
}: {
	collections: Collection[]
	tokens: Collection[]
}) => {
	const items = useMemo(() => {
		const nft = sortMarkets(collections, '7d', true)
			.filter((market) => volumeForPeriod(market, '7d', true) > 0)
			.slice(0, 11)
		const fungible = sortMarkets(tokens, '7d', false)
			.filter((market) => volumeForPeriod(market, '7d', false) > 0)
			.slice(0, 1)
		return [...nft.slice(0, 4), ...fungible, ...nft.slice(4)]
	}, [collections, tokens])

 const railRef = useRef<HTMLElement>(null)
 const dragRef = useRef({ active: false, moved: false, startX: 0, startScroll: 0 })
 const [ready, setReady] = useState(false)
 const [visible, setVisible] = useState(false)
 const [initialOrder] = useState(() => items.map(item => item.slug))
 const orderedItems = [...items].sort((a,b) => {
  const ai = initialOrder.indexOf(a.slug), bi = initialOrder.indexOf(b.slug)
  return (ai < 0 ? initialOrder.length : ai) - (bi < 0 ? initialOrder.length : bi)
 })
 const imageKey = orderedItems.map(item => `${item.slug}:${item.icon}`).join('|')
 useEffect(() => {
  const rail = railRef.current
  if (!rail) return
  let cancelled = false
  const images = Array.from(rail.querySelectorAll<HTMLImageElement>('img'))
  Promise.allSettled(images.map(img => { img.loading = 'eager'; return img.decode() })).then(() => { if (!cancelled) setReady(true) })
  let intersecting = false
  const update = () => setVisible(intersecting && !document.hidden)
  const observer = new IntersectionObserver(([entry]) => { intersecting = entry.isIntersecting; update() })
  observer.observe(rail)
  document.addEventListener('visibilitychange', update)
  return () => { cancelled = true; observer.disconnect(); document.removeEventListener('visibilitychange', update) }
 }, [imageKey])

 const onRailEnter = () => {
  const rail = railRef.current
  const track = rail?.querySelector<HTMLElement>('.market-marquee-track')
  if (!rail || !track || !window.matchMedia('(hover: hover)').matches) return
  if (marqueeEnabled) {
   const matrix = new DOMMatrixReadOnly(getComputedStyle(track).transform)
   rail.dataset.interacting = 'true'
   rail.scrollLeft = Math.max(0, -matrix.m41)
  }
  rail.style.cursor = 'grab'
 }
 const onRailDown = (event: React.MouseEvent<HTMLElement>) => {
  const rail = railRef.current
  if (!rail || event.button !== 0 || !window.matchMedia('(hover: hover)').matches) return
  event.preventDefault()
  dragRef.current = { active: true, moved: false, startX: event.clientX, startScroll: rail.scrollLeft }
  rail.style.cursor = 'grabbing'
 }
 const onRailMove = (event: React.MouseEvent<HTMLElement>) => {
  const rail = railRef.current
  const drag = dragRef.current
  if (!rail || !drag.active) return
  if (Math.abs(event.clientX - drag.startX) > 3) drag.moved = true
  rail.scrollLeft = drag.startScroll - (event.clientX - drag.startX)
 }
 const onRailUp = () => {
  dragRef.current.active = false
  if (railRef.current) railRef.current.style.cursor = 'grab'
 }
 const onRailLeave = () => {
  const rail = railRef.current
  const track = rail?.querySelector<HTMLElement>('.market-marquee-track')
  if (!rail) return
  onRailUp()
  rail.style.cursor = ''
  if (marqueeEnabled && track && rail.dataset.interacting === 'true') {
   const cycle = track.firstElementChild?.getBoundingClientRect().width || 1
   const offset = rail.scrollLeft % cycle
   rail.style.setProperty('--market-marquee-delay', `${(-offset / cycle) * 55}s`)
   rail.scrollLeft = 0
   delete rail.dataset.interacting
  }
 }
 const onRailClickCapture = (event: React.MouseEvent<HTMLElement>) => {
  if (!dragRef.current.moved) return
  event.preventDefault()
  event.stopPropagation()
  dragRef.current.moved = false
 }

	if (!items.length) return null
	const marqueeEnabled = items.length >= 6
	const tileClassName =
		'group relative aspect-square w-[136px] shrink-0 overflow-hidden market-gallery-tile rounded-xl bg-muted transition-colors duration-200 ease-out focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transform-none min-[480px]:w-[144px] md:w-[150px]'

	const renderItems = (duplicate = false) =>
		orderedItems.map((market) => {
			const protocol = protocolFromSlug(market.slug)
			const badgeLabel = protocol === 'ordinal' ? 'Trending' : protocolLabel(market.slug)
			const badgeColor = 'bg-gradient-to-r from-purple-600 to-blue-500'
			const tile = (
				<>
					<div className="market-gallery-art absolute inset-0">
						<CollectionIcon
							thumbnail
							collection={market}
							className="size-full"
							imageClassName="size-full object-cover"
							inscriptionClassName="size-full object-cover"
						/>
						<div className="absolute inset-0 bg-gradient-to-t from-black from-5% via-black/35 via-60% to-transparent" />
					</div>
					<span
						className={`absolute left-3 top-2 inline-flex rounded px-1.5 py-1 tabular-nums text-[10px] font-semibold leading-none text-white ${badgeColor}`}
					>
						{badgeLabel}
					</span>
					<div className="absolute inset-x-0 bottom-0 p-3">
						<span translate="no" className="line-clamp-3 block [overflow-wrap:anywhere] tabular-nums text-xs font-bold leading-tight text-white drop-shadow-md">
							{market.name}
						</span>
					</div>
				</>
			)

			if (duplicate) {
				return (
					<Link
                        prefetch={false}
						key={`duplicate-${market.slug}`}
						href={`/collection/${market.slug}`}
						className={tileClassName}
						tabIndex={-1}
						aria-hidden="true"
					>
						{tile}
					</Link>
				)
			}

			return (
				<Link
                        prefetch={false}
					key={market.slug}
					href={`/collection/${market.slug}`}
					aria-label={`${market.name}, ${protocolLabel(market.slug)}, trending seven-day market`}
					className={tileClassName}
				>
					{tile}
				</Link>
			)
		})

	return (
		<section
            ref={railRef}
            data-running={ready && visible}
			onMouseEnter={onRailEnter}
			onMouseDown={onRailDown}
			onMouseMove={onRailMove}
			onMouseUp={onRailUp}
			onMouseLeave={onRailLeave}
			onClickCapture={onRailClickCapture}
			onDragStart={(event) => event.preventDefault()}
			className={`market-marquee relative mx-0 sm:-mx-6 lg:-mx-8 2xl:-mx-10 ${
				!marqueeEnabled ? 'market-marquee--static' : ''
			}`}
			aria-labelledby="trending-markets-heading"
		>
			<h3 id="trending-markets-heading" className="sr-only">
				Trending Bitcoin markets
			</h3>
			<p id="trending-markets-description" className="sr-only">
				Top seven-day Ordinal collections with the leading Bitcoin token market.
			</p>
			<div className="market-marquee-track" aria-describedby="trending-markets-description">
				<div className="flex shrink-0 gap-3 pl-4 pt-1 pr-3 sm:pl-0 md:gap-4 md:pr-4">{renderItems()}</div>
				{marqueeEnabled && (
					<div
						className="market-marquee-copy flex shrink-0 gap-3 pt-1 pr-3 md:gap-4 md:pr-4"
						aria-hidden="true"
					>
						{renderItems(true)}
					</div>
				)}
			</div>
		</section>
	)
}

const MarketStats = ({
	markets,
	period,
	global
}: {
	markets: Collection[]
	period: Period
	global: boolean
}) => {
	const totalVolume = markets.reduce(
		(total, market) => total + volumeForPeriod(market, period, global),
		0
	)
	const listed = markets.reduce((total, market) => total + finiteNumber(market.listed), 0)

	return (
		<div className="grid grid-cols-3 gap-2 sm:flex sm:items-center sm:gap-8">
			<div>
				<p className="block tabular-nums text-sm font-semibold text-foreground sm:text-base">
					{formatVolume(totalVolume)}
				</p>
				<p className="mt-0.5 block text-[10px] text-muted-foreground">
					{period} {global ? 'volume' : 'OW volume'}
				</p>
			</div>
			<div className="text-center sm:text-left">
				<p className="block tabular-nums text-sm font-semibold text-foreground sm:text-base">
					{formatCount(listed)}
				</p>
				<p className="mt-0.5 block text-[10px] text-muted-foreground">
					OW listings
				</p>
			</div>
			<div className="text-right sm:text-left">
				<p className="block tabular-nums text-sm font-semibold text-foreground sm:text-base">
					{markets.length}
				</p>
				<p className="mt-0.5 block text-[10px] text-muted-foreground">
					Markets
				</p>
			</div>
		</div>
	)
}

const MarketTable = ({
	markets,
	period,
	global,
	fungible
}: {
	markets: Collection[]
	period: Period
	global: boolean
	fungible: boolean
}) => {
	const { exchangeRate } = useExchangeRate()
	const visible = markets.slice(0, 10)
	const maximumVolume = visible.reduce(
		(maximum, market) => Math.max(maximum, volumeForPeriod(market, period, global)),
		0
	)

	return (
		<div>
			<div className="overflow-x-auto">
				<table className="mobile-market-table w-full min-w-[782px] table-fixed text-left text-xs md:min-w-[900px]">
					<caption className="sr-only">Ordinal collection floor prices and seven-day volume</caption>
					<thead><tr className="border-b border-border/70 text-[10px] text-muted-foreground">
						<th className="w-[34px] py-3 pl-4">#</th>
						<th className="w-[160px] py-3 pr-2">Market</th>
						<th className="w-[103px] px-2 py-3">Price</th>
						<th className="w-[115px] px-2 py-3">7d vol</th>
						<th className="w-[120px] px-3 py-3">OW listed</th>
						<th className="w-[110px] px-3 py-3">Supply</th>
						<th className="w-[140px] px-3 py-3">Relative vol</th>
					</tr></thead>
					<tbody>{visible.map((market, index) => {
						// The fair value -- where an ordinary piece has been clearing,
						// the number the 7d change under it moves -- with the floor
						// standing in until the hub has a mark.
						const floor = marketWorth(market, fungible) ?? 0
						const volume = volumeForPeriod(market, period, global)
						const change = normalizeChange(changeForPeriod(market, period))
						const btcValue = (sats: number) => sats > 0 ? (sats / 1e8).toLocaleString('en-US', { minimumFractionDigits: sats < 1e6 ? 4 : 2, maximumFractionDigits: sats < 1e6 ? 6 : 2 }) : '—'
						// A price wants more than a volume: 0.0211 is a fair value, 0.02 is a bucket.
						const priceValue = (sats: number) => sats > 0 ? (sats / 1e8).toLocaleString('en-US', { minimumFractionDigits: sats < 1e6 ? 4 : 3, maximumFractionDigits: sats < 1e6 ? 6 : 4 }) : '—'
						return <tr key={market.slug} className="border-b border-border/50 last:border-0">
							<td className="py-3 pl-4 text-muted-foreground tabular-nums">{index + 1}</td>
							<td className="py-3 pr-2"><Link prefetch={false} href={`/collection/${market.slug}`} className="flex min-w-0 items-center gap-2"><CollectionIcon collection={market} className="size-7 shrink-0 overflow-hidden rounded-md bg-muted" imageClassName="size-full object-cover" inscriptionClassName="size-full object-cover" /><span className="min-w-0 truncate font-semibold">{market.name}</span></Link></td>
							<td className="px-2 py-3 tabular-nums"><span className="flex items-center gap-0.5 whitespace-nowrap font-semibold"><BitcoinIcon className="size-3 shrink-0 text-orange-500" aria-hidden="true" />{priceValue(floor)}</span><span className={`block text-[10px] ${change === null || change === 0 ? 'text-muted-foreground' : change > 0 ? 'text-emerald-500' : 'text-rose-500'}`}>{change === null ? '—' : `${change < 0 ? '-' : ''}${Math.abs(change).toFixed(2)}%`}</span></td>
							<td className="px-2 py-3 tabular-nums"><span className="flex items-center gap-0.5 whitespace-nowrap font-semibold"><BitcoinIcon className="size-3 shrink-0 text-orange-500" aria-hidden="true" />{btcValue(volume)}</span><span className="block text-[10px] text-muted-foreground">{exchangeRate && volume > 0 ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(volume / 1e8 * exchangeRate) : '—'}</span></td>
							<td className="px-3 py-3 tabular-nums">{formatCount(finiteNumber(market.listed))}</td>
							<td className="px-3 py-3 tabular-nums">{formatCount(finiteNumber(market.total_supply))}</td>
							<td className="px-3 py-3"><RelativeVolumeBar value={volume} max={maximumVolume} /></td>
						</tr>
					})}</tbody>
				</table>
			</div>
			{visible.length === 0 ? (
				<div className="flex min-h-48 items-center justify-center px-6 text-sm text-muted-foreground">
					No active markets in this view yet.
				</div>
			) : null}
		</div>
	)
}

const SalesRail = ({ sales }: { sales: any[] }) => {
 const openInscription = useInscriptionModal()
	// This rail sits under the Ordinals table: collection sales only. The
	// global tape also carries runes and tokens, which have their own panels.
	const allItems = sales.filter(
		(sale) => sale?.collection?.slug && !isFungible(sale.collection.slug) && getSalePriceSats(sale) > 0
	)
	const items = allItems.slice(0, 7)
	const topSales = allItems
		.slice()
		.sort((a, b) => getSalePriceSats(b) - getSalePriceSats(a))
		.slice(0, 6)

	return (
		<aside
			className="border-t border-border/70 bg-muted/10 2xl:border-l 2xl:border-t-0"
			aria-label="Ordinal sales activity"
		>
			<div className="market-sales-header flex items-center justify-between border-b border-border/70 px-4">
				<div className="flex items-center gap-2">
					<Radio className="size-3.5 text-purple-600 dark:text-purple-400" aria-hidden="true" />
					<h4 className="text-xs font-semibold text-foreground">Recent sales</h4>
				</div>
				<Link
                        prefetch={false}
					href="/activity"
					className="text-[10px] font-semibold text-muted-foreground hover:text-foreground"
				>
					View all
				</Link>
			</div>
			<div className="divide-y divide-border/50">
				{items.slice(0, 5).map((sale) => (
					<Link
                        prefetch={false}
						key={`${sale.id}-${getSaleBoughtAt(sale)}`}
						href={saleCollectionHref(sale)}
						className="group flex items-center gap-2.5 px-4 py-2.5 transition-colors hover:bg-muted/40"
					>
						<div className="size-8 shrink-0 overflow-hidden rounded-md bg-muted">
							{!sale.id && sale.collection ? (
								<CollectionIcon
									collection={sale.collection}
									className="size-full"
									imageClassName="size-full object-cover"
									inscriptionClassName="size-full object-cover"
								/>
							) : (
								<InscriptionContent
									className="size-full overflow-hidden rounded-md"
									inscription={sale}
									isDetail={false}
									showPricePer={false}
									smallCard={true}
								/>
							)}
						</div>
						<div className="min-w-0 flex-1">
							<p className="block truncate text-[11px] font-semibold text-foreground group-hover:text-muted-foreground">
								{saleName(sale)}
							</p>
							<p className="block truncate text-[9px] text-muted-foreground">
								{sale?.collection?.name || 'Ordinal'}
							</p>
						</div>
						<div className="text-right">
							<p className="flex items-center justify-end gap-1 whitespace-nowrap tabular-nums text-[10px] font-semibold text-purple-600 dark:text-purple-400">
                                <SaleMarketplaceIcon sale={sale} />
								{formatSalePrice(getSalePriceSats(sale))}
							</p>
							<p className="block text-[8px] text-muted-foreground">
								{formatSaleTime(getSaleBoughtAt(sale))}
							</p>
						</div>
					</Link>
				))}
			</div>
			<div className="border-t border-border/70 p-4">
				<p className="mb-2.5 text-[10px] font-semibold text-muted-foreground">
					Top recent sales
				</p>
				<div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-1.5">
					{topSales.map((sale) => (
						<button
                            type="button"
							key={`top-${sale.id}-${getSaleBoughtAt(sale)}`}
							onClick={() => openInscription({ id: sale.id, inscription: sale })}
							aria-label={`${saleName(sale)}, ${formatSalePrice(getSalePriceSats(sale))}`}
							className="group relative aspect-square overflow-hidden rounded-md bg-muted text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
						>
							{!sale.id && sale.collection ? (
								<CollectionIcon
									collection={sale.collection}
									className="size-full transition-transform duration-300 group-hover:scale-105"
									imageClassName="size-full object-cover"
									inscriptionClassName="size-full object-cover"
								/>
							) : (
								<InscriptionContent
									className="size-full overflow-hidden rounded-md transition-transform duration-300 group-hover:scale-105"
									inscription={sale}
									isDetail={false}
									showPricePer={false}
									smallCard={true}
								/>
							)}
							<div className="pointer-events-none absolute inset-x-0 top-0 bg-gradient-to-b from-black/80 to-transparent px-2 pb-5 pt-2 text-[11px] font-semibold leading-tight text-white opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100 motion-reduce:transition-none">
                                    {sale?.collection?.name || 'Ordinal'}
                                </div>
                                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-1.5 pb-1 pt-4 tabular-nums text-[10px] sm:text-[8px] font-semibold text-white">
								{formatSalePrice(getSalePriceSats(sale))}
							</div>
						</button>
					))}
				</div>
			</div>
		</aside>
	)
}

const MarketPanelHeader = ({
	headingId,
	kicker,
	title,
	markets,
	period,
	global,
	ctaHref,
	ctaLabel,
	children
}: {
	headingId: string
	kicker: string
	title: string
	markets: Collection[]
	period: Period
	global: boolean
	ctaHref: string
	ctaLabel: string
	children?: React.ReactNode
}) => (
	<header className="relative p-4 sm:p-5 lg:p-6">
		<div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
			<div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-start sm:gap-8">
				<div className="min-w-0 sm:min-w-[190px]">
					<div className="mb-1">
						<p className="text-[10px] font-bold text-purple-600 dark:text-purple-400">
							{kicker}
						</p>
						<div className="absolute right-4 top-4 sm:hidden"><ExploreLink href={ctaHref}>{ctaLabel}</ExploreLink></div>
					</div>
					<h3 id={headingId} className="text-xl font-semibold tracking-tight text-foreground">
						{title}
					</h3>
				</div>
				<MarketStats markets={markets} period={period} global={global} />
			</div>
			<div className="hidden flex-wrap items-center gap-2 sm:flex xl:justify-end">
				<span className="text-[11px] font-medium text-muted-foreground">
					Past 7 days
				</span>
				<ExploreLink href={ctaHref}>{ctaLabel}</ExploreLink>
			</div>
		</div>
		{children ? <div className="mt-5">{children}</div> : null}
	</header>
)

const MarketOverview = ({
	collections: initialCollections = EMPTY_COLLECTIONS,
	tokens: initialTokens = EMPTY_COLLECTIONS,
	recentSales: initialSales = EMPTY_SALES,
 runes: initialRunes = null
}: MarketOverviewProps) => {
 const { data: sales, error: salesError } = useSWR(
  `${API_URL}/inscriptions/global-activity?offset=0&limit=100`,
  async (url: string) => {
   const rows = await correctedGlobalActivityFetcher(url)
   if (!Array.isArray(rows)) throw new Error('Sales unavailable')
   return rows
  },
  { fallbackData: initialSales, refreshInterval: 10000, dedupingInterval: 5000, revalidateOnMount: true, revalidateOnFocus: true, revalidateOnReconnect: true, refreshWhenHidden: false }
 )
 const { data: markets, error: marketsError } = useSWR<{ collections: Collection[]; tokens: Collection[] }>(
  '/api/home/markets', async (url: string) => {
   const response = await fetch(url)
   if (!response.ok) throw new Error('Markets unavailable')
   return response.json()
  },
  { fallbackData: { collections: initialCollections, tokens: initialTokens }, refreshInterval: 60000, dedupingInterval: 30000, revalidateOnMount: true, revalidateOnFocus: true, revalidateOnReconnect: true, refreshWhenHidden: false }
 )
 // A figure already on screen stays through a refresh that lacks it. The
 // API computes the 7d change from a tape it keeps in memory, and for a
 // minute after a deploy some rows come back without one; swapping a
 // number for "—" and back is what the visitor sees as flicker.
 const shownChange = useRef(new Map<string, number>())
 const collections = useMemo(() => {
  const rows = markets?.collections ?? initialCollections
  return rows.map((row) => {
   const fair = row.change_week_fair
   if (typeof fair === 'number' && Number.isFinite(fair)) {
    shownChange.current.set(row.slug, fair)
    return row
   }
   const kept = shownChange.current.get(row.slug)
   return kept === undefined ? row : { ...row, change_week_fair: kept }
  })
 }, [markets, initialCollections])
 const tokens = markets?.tokens ?? initialTokens
 const recentSales = sales ?? initialSales
 const period: Period = '7d'

	const collectionMarkets = useMemo(
		() => sortMarkets(collections, period, true),
		[collections]
	)


	return (
		<section
			className="mx-auto w-full max-w-[1920px] px-0 py-8 sm:px-6 lg:px-8 2xl:px-10"
			aria-labelledby="market-overview-heading"
		>
			<div className="mb-5 flex flex-col gap-3 px-4 sm:px-0 lg:flex-row lg:items-end lg:justify-between">
				<div>
					<h2
						id="market-overview-heading"
						className="max-w-[19rem] text-2xl font-semibold tracking-tight text-foreground sm:max-w-none sm:text-3xl"
					>
						Trade what&apos;s moving on Bitcoin
					</h2>
					<p className="mt-1 max-w-2xl text-sm text-muted-foreground">
						Discover collections and tokens. Compare prices, trading volume, and recent sales.
						{(salesError || marketsError) && <span className="mt-1 block text-xs text-muted-foreground" role="status">
                            Updates delayed · showing last available data
                        </span>}
					</p>
				</div>
			</div>

			<div className="space-y-4">
				<div className="pb-4 sm:pb-6"><TrendingRail collections={collections} tokens={tokens} /></div>

				<section
					className="market-surface overflow-hidden"
					aria-labelledby="ordinal-market-heading"
				>
					<MarketPanelHeader
						headingId="ordinal-market-heading"
						kicker="Global Market Data"
						title="Ordinals"
						markets={collectionMarkets}
						period={period}
						global={true}
						ctaHref="/collect?c=collections&s=GlobalVolumeWeekDesc"
						ctaLabel="Explore Ordinals"
					/>
					<div className="grid grid-cols-[minmax(0,1fr)] 2xl:grid-cols-[minmax(0,1fr)_320px]">
						<MarketTable
							markets={collectionMarkets}
							period={period}
							global={true}
							fungible={false}
						/>
						<SalesRail sales={recentSales} />
					</div>
				</section>

				<RuneMarkets initialData={initialRunes} tokens={tokens} />
			</div>


		</section>
	)
}

export default MarketOverview
